// src/base/css-variables.ts
function computeBorder(style) {
  const { borderWidth, borderType, borderColor } = style;
  if (!borderType || borderType === "none") return "none";
  return `${borderWidth ?? 0}px ${borderType} ${borderColor ?? "#212121"}`;
}
function computeTextDecoration(style) {
  const parts = [];
  if (style.isUnderLine) parts.push("underline");
  if (style.isDelLine) parts.push("line-through");
  return parts.length > 0 ? parts.join(" ") : "none";
}
function applyCssVars(el, style) {
  for (const [property, value] of Object.entries(componentStyleToCssVariables(style))) {
    el.style.setProperty(property, value);
  }
}
function componentStyleToCssVariables(style) {
  return {
    "--ptd-width": `${style.width}px`,
    "--ptd-height": `${style.height}px`,
    "--ptd-rotate": `${style.rotate ?? 0}deg`,
    "--ptd-opacity": String(style.opacity ?? 1),
    "--ptd-color": style.color ?? "#212121",
    "--ptd-background": style.background ?? "transparent",
    "--ptd-font-size": `${style.fontSize ?? 12}pt`,
    "--ptd-font-family": style.fontFamily && style.fontFamily !== "default" ? style.fontFamily : "inherit",
    "--ptd-font-weight": style.fontWeight ?? "normal",
    "--ptd-font-style": style.fontStyle ?? "normal",
    "--ptd-line-height": style.lineHeight ?? "1",
    "--ptd-letter-spacing": `${style.letterSpacing ?? 0}px`,
    "--ptd-padding": `${style.padding ?? 0}px`,
    "--ptd-margin": `${style.margin ?? 0}px`,
    "--ptd-border": computeBorder(style),
    "--ptd-border-radius": style.borderRadius ?? "inherit",
    "--ptd-justify-content": style.justifyContent ?? "flex-start",
    "--ptd-align-items": style.alignItems ?? "flex-start",
    "--ptd-text-decoration": computeTextDecoration(style),
    "--ptd-border-width": `${style.borderWidth ?? 0}px`,
    "--ptd-border-color": style.borderColor ?? "#212121",
    "--ptd-border-spacing": `${style.borderWidth ?? 0}px`
  };
}

// src/base/stylesheet.ts
var STYLE_ID = "ptd-components-styles";
var CSS = `
.ptd-component {
  box-sizing: border-box;
  position: absolute;
  width: var(--ptd-width);
  height: var(--ptd-height);
  transform: rotate(var(--ptd-rotate, 0deg));
  opacity: var(--ptd-opacity, 1);
  overflow: hidden;
}

.ptd-simple-text {
  color: var(--ptd-color);
  background: var(--ptd-background);
  border: var(--ptd-border);
  border-radius: var(--ptd-border-radius);
  font-size: var(--ptd-font-size);
  font-family: var(--ptd-font-family);
}

.ptd-simple-text__inner {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  word-break: break-all;
  justify-content: var(--ptd-justify-content);
  align-items: var(--ptd-align-items);
  padding: var(--ptd-padding);
  line-height: var(--ptd-line-height);
  letter-spacing: var(--ptd-letter-spacing);
  font-weight: var(--ptd-font-weight);
  font-style: var(--ptd-font-style);
  text-decoration: var(--ptd-text-decoration);
}

.ptd-text {
  color: var(--ptd-color);
  background: var(--ptd-background);
  border-radius: var(--ptd-border-radius);
  border: var(--ptd-border);
}

.ptd-text__inner {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: var(--ptd-padding);
  line-height: var(--ptd-line-height);
  letter-spacing: var(--ptd-letter-spacing);
  font-size: var(--ptd-font-size);
  font-family: var(--ptd-font-family);
}

.ptd-text__inner table {
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 1px;
  background-color: #000;
  padding: 1px;
}

.ptd-text__inner td {
  position: relative;
  background-color: #fff;
  overflow: hidden;
}

.ptd-text__inner p,
.ptd-text__inner h1,
.ptd-text__inner h2,
.ptd-text__inner h3,
.ptd-text__inner h4 {
  margin-block-start: 0;
  margin-block-end: 0;
}

.ptd-text__inner ul,
.ptd-text__inner ol {
  margin-block-start: 0;
  margin-block-end: 0;
  padding-inline-start: 1.5em;
}

.ptd-text__inner blockquote {
  margin-block-start: 0;
  margin-block-end: 0;
  padding-inline-start: 0.8em;
}

.ptd-line {
  background: var(--ptd-background);
}

.ptd-rect {
  background: var(--ptd-background);
  border: var(--ptd-border);
  border-radius: var(--ptd-border-radius);
}

.ptd-circle {
  background: var(--ptd-background);
  border: var(--ptd-border);
  border-radius: 100%;
}

.ptd-star {
  background: transparent;
}

.ptd-star__svg {
  width: 100%;
  height: 100%;
  display: block;
  fill: var(--ptd-background);
  pointer-events: none;
}

.ptd-image {
  background: var(--ptd-background);
  border: var(--ptd-border);
  border-radius: var(--ptd-border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
}

.ptd-image img {
  width: 100%;
  height: 100%;
  display: block;
}

.ptd-qrcode {
  background: var(--ptd-background);
  border: var(--ptd-border);
}

.ptd-qrcode canvas,
.ptd-qrcode img {
  width: 100%;
  height: 100%;
}

.ptd-qrcode__inner,
.ptd-barcode__inner {
  width: 100%;
  height: 100%;
}

.ptd-barcode {
  background: var(--ptd-background);
  border: var(--ptd-border);
  position: relative;
}

.ptd-barcode canvas,
.ptd-barcode img {
  width: 100%;
  height: 100%;
}

.ptd-render-state {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: grid;
  place-items: center;
  padding: 8px;
  color: #647184;
  background: rgb(231 236 243 / 46%);
  font-family: system-ui, sans-serif;
  font-size: 11px;
  line-height: 1.35;
  text-align: center;
}

.ptd-render-state[data-state='error'] {
  color: #9f3523;
  background: rgb(207 77 52 / 8%);
}

.ptd-group {
  position: absolute;
}

.ptd-simple-table {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.ptd-simple-table table {
  width: 100%;
  height: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.ptd-simple-table td {
  position: relative;
  box-sizing: border-box;
  padding: 0;
  border: var(--ptd-table-border-width) var(--ptd-table-border-style)
    var(--ptd-table-border-color);
  color: var(--ptd-table-color);
  background: var(--ptd-table-background);
  font-family: var(--ptd-table-font-family);
  font-size: var(--ptd-table-font-size);
  font-style: var(--ptd-table-font-style);
  font-weight: var(--ptd-table-font-weight);
  text-align: var(--ptd-table-horizontal-align);
  vertical-align: var(--ptd-table-vertical-align);
  overflow: hidden;
}

.ptd-simple-table__cell-content {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: var(--ptd-table-padding);
  overflow: hidden;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  text-decoration: var(--ptd-table-text-decoration);
}

.ptd-complex-table {
  color: var(--ptd-color);
  background: var(--ptd-background);
  font-size: var(--ptd-font-size);
  font-family: var(--ptd-font-family);
  position: relative;
}

.ptd-complex-table table {
  width: 100%;
}

.ptd-complex-table th {
  text-align: center;
  font-weight: bold;
  padding: 0;
  word-break: break-all;
}

.ptd-complex-table td {
  padding: 0;
  word-break: break-all;
  position: relative;
}

.ptd-complex-table__body table {
  border-collapse: separate;
  border-spacing: var(--ptd-border-spacing);
  background-color: var(--ptd-border-color);
}

.ptd-complex-table__body td,
.ptd-complex-table__body th {
  position: relative;
  background-color: var(--ptd-background, #fff);
}
`;
function injectStylesheet() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

// src/base/base-component.ts
var BaseComponent = class {
  container;
  schema;
  constructor(schema) {
    this.schema = schema;
    this.container = document.createElement("div");
    this.container.classList.add("ptd-component");
    injectStylesheet();
    this.applyStyles(schema.style);
    this.render();
  }
  mount(parent) {
    parent.appendChild(this.container);
  }
  update(schema) {
    this.schema = schema;
    this.applyStyles(schema.style);
    this.render();
  }
  destroy() {
    this.container.remove();
  }
  applyStyles(style) {
    applyCssVars(this.container, style);
  }
};

// src/components/RoyBarCode.ts
import { barCodeContentError, normalizeBarCodeProps } from "@ptd/core";
var renderSessions = /* @__PURE__ */ new WeakMap();
var RoyBarCode = class extends BaseComponent {
  constructor(schema) {
    super(schema);
  }
  render() {
    this.container.classList.add("ptd-barcode");
    let barContainer = this.container.querySelector(".ptd-barcode__inner");
    if (!barContainer) {
      barContainer = document.createElement("div");
      barContainer.className = "ptd-barcode__inner";
      this.container.appendChild(barContainer);
    }
    this.renderBarCode(barContainer);
  }
  renderBarCode(target) {
    const session = { target };
    renderSessions.set(this, session);
    const props = normalizeBarCodeProps(this.schema.propValue);
    const contentError = barCodeContentError(props);
    if (contentError) {
      setBarCodeStatus(this.container, target, "error", contentError);
      return;
    }
    setBarCodeStatus(this.container, target, "loading", "\u6B63\u5728\u751F\u6210\u6761\u5F62\u7801\u2026");
    import("bwip-js").then((mod) => {
      if (renderSessions.get(this) !== session) return;
      const bwipjs = mod.default ?? mod;
      const canvas = document.createElement("canvas");
      try {
        bwipjs.toCanvas(canvas, {
          bcid: props.bcid,
          text: props.text,
          scale: 2,
          barcolor: withoutHash(props.colorDark),
          textcolor: withoutHash(props.colorDark),
          backgroundcolor: backgroundColor(this.schema.style.background),
          includetext: props.includeText,
          textxalign: "center",
          textsize: 10
        });
        target.replaceChildren(canvas);
        this.container.dataset.renderState = "ready";
      } catch {
        setBarCodeStatus(this.container, target, "error", "\u6761\u5F62\u7801\u5185\u5BB9\u4E0D\u7B26\u5408\u5F53\u524D\u7801\u5236");
      }
    }).catch(() => {
      if (renderSessions.get(this) === session) {
        setBarCodeStatus(this.container, target, "error", "\u6761\u5F62\u7801\u6E32\u67D3\u6A21\u5757\u8F7D\u5165\u5931\u8D25");
      }
    });
  }
  destroy() {
    renderSessions.delete(this);
    super.destroy();
  }
};
function withoutHash(value) {
  return value.replace(/^#/, "");
}
function backgroundColor(value) {
  return typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value) ? withoutHash(value) : "ffffff";
}
function setBarCodeStatus(container, target, state, message) {
  const status = document.createElement("div");
  status.className = "ptd-render-state";
  status.dataset.state = state;
  status.textContent = message;
  target.replaceChildren(status);
  container.dataset.renderState = state;
}

// src/components/RoyCircle.ts
var RoyCircle = class extends BaseComponent {
  constructor(schema) {
    super(schema);
  }
  render() {
    this.container.classList.add("ptd-circle");
  }
};

// src/components/RoyComplexTable.ts
var RoyComplexTable = class extends BaseComponent {
  constructor(schema) {
    super(schema);
  }
  render() {
    this.container.classList.add("ptd-complex-table");
    this.container.innerHTML = "";
    const propValue = this.schema.propValue;
    if (!propValue) return;
    if (propValue.header) {
      const section = this.renderSection(propValue.header, "thead");
      section.classList.add("ptd-complex-table__header");
      this.container.appendChild(section);
    }
    if (propValue.body) {
      const section = this.renderSection(propValue.body, "tbody");
      section.classList.add("ptd-complex-table__body");
      this.container.appendChild(section);
    }
    if (propValue.footer) {
      const section = this.renderSection(propValue.footer, "tfoot");
      section.classList.add("ptd-complex-table__footer");
      this.container.appendChild(section);
    }
  }
  renderSection(section, tagName) {
    const table = document.createElement("table");
    const sectionEl = document.createElement(tagName);
    table.appendChild(sectionEl);
    for (const row of section.rows) {
      const tr = document.createElement("tr");
      for (const cell of row) {
        const td = document.createElement(tagName === "thead" ? "th" : "td");
        if (cell.colSpan && cell.colSpan > 1) td.colSpan = cell.colSpan;
        if (cell.rowSpan && cell.rowSpan > 1) td.rowSpan = cell.rowSpan;
        td.innerHTML = cell.content ?? "";
        tr.appendChild(td);
      }
      sectionEl.appendChild(tr);
    }
    return table;
  }
};

// src/components/RoyGroup.ts
var RoyGroup = class extends BaseComponent {
  children = [];
  constructor(schema) {
    super(schema);
  }
  render() {
    this.container.classList.add("ptd-group");
  }
  addChild(child) {
    this.children.push(child);
    child.mount(this.container);
  }
  destroy() {
    for (const child of this.children) {
      child.destroy();
    }
    this.children = [];
    super.destroy();
  }
};

// src/components/RoyImage.ts
import { imageSourceError, normalizeImageProps } from "@ptd/core";
var renderSessions2 = /* @__PURE__ */ new WeakMap();
var RoyImage = class extends BaseComponent {
  constructor(schema) {
    super(schema);
  }
  render() {
    this.container.classList.add("ptd-image");
    invalidateSession(this);
    const props = normalizeImageProps(this.schema.propValue);
    const sourceError = imageSourceError(props.src);
    if (props.src.trim() === "") {
      setStatus(this.container, "empty", "\u9009\u62E9\u56FE\u7247\u6216\u8F93\u5165\u56FE\u7247\u5730\u5740");
      return;
    }
    if (sourceError) {
      setStatus(this.container, "error", sourceError);
      return;
    }
    const image = document.createElement("img");
    const session = { image };
    renderSessions2.set(this, session);
    image.alt = props.alt;
    image.style.objectFit = props.fit;
    image.style.objectPosition = props.position;
    setStatus(this.container, "loading", "\u6B63\u5728\u8F7D\u5165\u56FE\u7247\u2026");
    image.onload = () => {
      if (renderSessions2.get(this) !== session) return;
      clearImageHandlers(image);
      session.image = null;
      this.container.replaceChildren(image);
      this.container.dataset.renderState = "ready";
    };
    image.onerror = () => {
      if (renderSessions2.get(this) !== session) return;
      clearImageHandlers(image);
      session.image = null;
      setStatus(this.container, "error", "\u56FE\u7247\u8F7D\u5165\u5931\u8D25\uFF0C\u8BF7\u68C0\u67E5\u5730\u5740\u6216\u91CD\u65B0\u9009\u62E9");
    };
    image.src = props.src;
  }
  destroy() {
    invalidateSession(this);
    renderSessions2.delete(this);
    super.destroy();
  }
};
function setStatus(container, state, message) {
  const status = document.createElement("div");
  status.className = "ptd-render-state";
  container.dataset.renderState = state;
  status.dataset.state = state;
  status.textContent = message;
  container.replaceChildren(status);
}
function invalidateSession(component) {
  const session = renderSessions2.get(component);
  if (session?.image) clearImageHandlers(session.image);
  renderSessions2.delete(component);
}
function clearImageHandlers(image) {
  image.onload = null;
  image.onerror = null;
}

// src/components/RoyLine.ts
var RoyLine = class extends BaseComponent {
  constructor(schema) {
    super(schema);
  }
  render() {
    this.container.classList.add("ptd-line");
  }
};

// src/components/RoyQRCode.ts
import {
  normalizeQRCodeProps,
  qrCodeContentError
} from "@ptd/core";
var CORRECTION_LEVEL = { L: 1, M: 0, Q: 3, H: 2 };
var renderSessions3 = /* @__PURE__ */ new WeakMap();
var RoyQRCode = class extends BaseComponent {
  constructor(schema) {
    super(schema);
  }
  render() {
    this.container.classList.add("ptd-qrcode");
    let qrContainer = this.container.querySelector(".ptd-qrcode__inner");
    if (!qrContainer) {
      qrContainer = document.createElement("div");
      qrContainer.className = "ptd-qrcode__inner";
      qrContainer.style.width = "100%";
      qrContainer.style.height = "100%";
      this.container.appendChild(qrContainer);
    }
    this.renderQRCode(qrContainer);
  }
  renderQRCode(target) {
    const session = { target };
    renderSessions3.set(this, session);
    const props = normalizeQRCodeProps(this.schema.propValue);
    const contentError = qrCodeContentError(props);
    if (contentError) {
      setCodeStatus(this.container, target, "error", contentError);
      return;
    }
    setCodeStatus(this.container, target, "loading", "\u6B63\u5728\u751F\u6210\u4E8C\u7EF4\u7801\u2026");
    import("easyqrcodejs").then((mod) => {
      if (renderSessions3.get(this) !== session) return;
      const QRCode = mod.default ?? mod;
      target.replaceChildren();
      try {
        new QRCode(target, {
          text: props.text,
          width: this.schema.style.width,
          height: this.schema.style.height,
          colorDark: props.colorDark,
          colorLight: props.colorLight,
          correctLevel: CORRECTION_LEVEL[props.correctLevel],
          quietZone: props.margin
        });
        this.container.dataset.renderState = "ready";
      } catch {
        setCodeStatus(this.container, target, "error", "\u4E8C\u7EF4\u7801\u751F\u6210\u5931\u8D25\uFF0C\u8BF7\u7F29\u77ED\u6216\u68C0\u67E5\u5185\u5BB9");
      }
    }).catch(() => {
      if (renderSessions3.get(this) === session) {
        setCodeStatus(this.container, target, "error", "\u4E8C\u7EF4\u7801\u6E32\u67D3\u6A21\u5757\u8F7D\u5165\u5931\u8D25");
      }
    });
  }
  destroy() {
    renderSessions3.delete(this);
    super.destroy();
  }
};
function setCodeStatus(container, target, state, message) {
  const status = document.createElement("div");
  status.className = "ptd-render-state";
  status.dataset.state = state;
  status.textContent = message;
  target.replaceChildren(status);
  container.dataset.renderState = state;
}

// src/components/RoyRect.ts
var RoyRect = class extends BaseComponent {
  constructor(schema) {
    super(schema);
  }
  render() {
    this.container.classList.add("ptd-rect");
  }
};

// src/components/RoySimpleTable.ts
import {
  getTableCellBounds,
  normalizeSimpleTableProps
} from "@ptd/core";
var RoySimpleTable = class extends BaseComponent {
  constructor(schema) {
    super(schema);
  }
  render() {
    this.container.classList.add("ptd-simple-table");
    this.container.innerHTML = "";
    const value = normalizeSimpleTableProps(this.schema.propValue);
    const table = document.createElement("table");
    table.setAttribute("aria-label", this.schema.name ?? "\u81EA\u7531\u8868\u683C");
    table.dataset.ptdTable = "simple";
    const colgroup = document.createElement("colgroup");
    const totalWidth = sum(value.columnWidths);
    value.columnWidths.forEach((width) => {
      const column = document.createElement("col");
      column.style.width = `${width / totalWidth * 100}%`;
      colgroup.appendChild(column);
    });
    table.appendChild(colgroup);
    const tbody = document.createElement("tbody");
    table.appendChild(tbody);
    const totalHeight = sum(value.rowHeights);
    for (let row = 0; row < value.grid.length; row += 1) {
      const tr = document.createElement("tr");
      tr.style.height = `${(value.rowHeights[row] ?? 0) / totalHeight * 100}%`;
      for (let column = 0; column < value.columnWidths.length; column += 1) {
        const cellId = value.grid[row]?.[column];
        if (!cellId) continue;
        const bounds = getTableCellBounds(value, cellId);
        if (!bounds || bounds.startRow !== row || bounds.startColumn !== column) continue;
        const cell = value.cells[cellId];
        if (!cell) continue;
        const td = document.createElement("td");
        td.dataset.cellId = cellId;
        td.dataset.row = String(row);
        td.dataset.column = String(column);
        if (cell.colSpan > 1) td.colSpan = cell.colSpan;
        if (cell.rowSpan > 1) td.rowSpan = cell.rowSpan;
        applyCellVariables(td, cell.style);
        const inner = document.createElement("div");
        inner.className = "ptd-simple-table__cell-content";
        inner.textContent = cell.text;
        td.appendChild(inner);
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    this.container.appendChild(table);
  }
};
function applyCellVariables(cell, style) {
  cell.style.setProperty("--ptd-table-font-family", style.fontFamily);
  cell.style.setProperty("--ptd-table-font-size", `${style.fontSize}px`);
  cell.style.setProperty("--ptd-table-font-weight", style.fontWeight);
  cell.style.setProperty("--ptd-table-font-style", style.fontStyle);
  cell.style.setProperty("--ptd-table-text-decoration", style.textDecoration);
  cell.style.setProperty("--ptd-table-color", style.color);
  cell.style.setProperty("--ptd-table-background", style.background);
  cell.style.setProperty("--ptd-table-horizontal-align", style.horizontalAlign);
  cell.style.setProperty("--ptd-table-vertical-align", style.verticalAlign);
  cell.style.setProperty("--ptd-table-padding", `${style.padding}px`);
  cell.style.setProperty("--ptd-table-border-color", style.borderColor);
  cell.style.setProperty("--ptd-table-border-width", `${style.borderWidth}px`);
  cell.style.setProperty("--ptd-table-border-style", style.borderStyle);
}
function sum(values) {
  return values.reduce((total, value) => total + value, 0) || 1;
}

// src/components/RoySimpleText.ts
var RoySimpleText = class extends BaseComponent {
  inner;
  constructor(schema) {
    super(schema);
    this.inner = this.container.querySelector(".ptd-simple-text__inner");
  }
  render() {
    this.container.classList.add("ptd-simple-text");
    let inner = this.container.querySelector(".ptd-simple-text__inner");
    if (!inner) {
      inner = document.createElement("div");
      inner.className = "ptd-simple-text__inner";
      this.container.appendChild(inner);
    }
    this.inner = inner;
    const text = this.resolveText();
    inner.textContent = text;
  }
  resolveText() {
    const raw = typeof this.schema.propValue === "string" ? this.schema.propValue : "";
    return raw;
  }
  resolveWithData(engine) {
    const raw = typeof this.schema.propValue === "string" ? this.schema.propValue : "";
    if (this.inner) {
      this.inner.textContent = engine.resolve(raw);
    }
  }
};

// src/components/RoyStar.ts
var SVG_NAMESPACE = "http://www.w3.org/2000/svg";
var STAR_POINTS = "50,0 61.8,36.2 100,36.2 69.1,58.6 80.9,95 50,72.4 19.1,95 30.9,58.6 0,36.2 38.2,36.2";
var RoyStar = class extends BaseComponent {
  constructor(schema) {
    super(schema);
  }
  render() {
    this.container.classList.add("ptd-star");
    let svg = this.container.querySelector(".ptd-star__svg");
    if (!svg) {
      svg = document.createElementNS(SVG_NAMESPACE, "svg");
      svg.classList.add("ptd-star__svg");
      svg.setAttribute("viewBox", "0 0 100 100");
      svg.setAttribute("preserveAspectRatio", "none");
      svg.setAttribute("aria-hidden", "true");
      svg.setAttribute("focusable", "false");
      const polygon = document.createElementNS(SVG_NAMESPACE, "polygon");
      polygon.setAttribute("points", STAR_POINTS);
      svg.appendChild(polygon);
      this.container.replaceChildren(svg);
    }
  }
};

// src/components/richTextHtml.ts
var ALLOWED_TAGS = /* @__PURE__ */ new Set([
  "A",
  "BLOCKQUOTE",
  "BR",
  "EM",
  "H1",
  "H2",
  "H3",
  "H4",
  "LI",
  "OL",
  "P",
  "S",
  "SPAN",
  "STRONG",
  "U",
  "UL"
]);
var DROP_CONTENT_TAGS = /* @__PURE__ */ new Set(["IFRAME", "OBJECT", "SCRIPT", "STYLE", "SVG"]);
var ALLOWED_STYLES = /* @__PURE__ */ new Set([
  "background-color",
  "color",
  "font-family",
  "font-size",
  "line-height",
  "text-align"
]);
var ALLOWED_TEXT_ALIGN = /* @__PURE__ */ new Set(["center", "justify", "left", "right"]);
var SAFE_LENGTH = /^\d+(?:\.\d+)?(?:em|pt|px|rem|%)$/;
var SAFE_LINE_HEIGHT = /^(?:normal|\d+(?:\.\d+)?(?:em|px|rem|%)?)$/;
function sanitizeRichTextHtml(html) {
  if (typeof document === "undefined" || html.trim() === "") return "";
  const template = document.createElement("template");
  template.innerHTML = html;
  sanitizeChildren(template.content);
  return template.innerHTML;
}
function sanitizeChildren(parent) {
  for (const child of Array.from(parent.childNodes)) {
    if (!(child instanceof Element)) continue;
    if (DROP_CONTENT_TAGS.has(child.tagName)) {
      child.remove();
      continue;
    }
    if (!ALLOWED_TAGS.has(child.tagName)) {
      sanitizeChildren(child);
      child.replaceWith(...Array.from(child.childNodes));
      continue;
    }
    sanitizeElement(child);
    sanitizeChildren(child);
  }
}
function sanitizeElement(element) {
  const style = sanitizeStyle(element.getAttribute("style") ?? "");
  const href = element.tagName === "A" ? sanitizeLink(element.getAttribute("href") ?? "") : null;
  const target = element.tagName === "A" && element.getAttribute("target") === "_blank" ? "_blank" : null;
  for (const attribute of Array.from(element.attributes)) element.removeAttribute(attribute.name);
  if (style) element.setAttribute("style", style);
  if (!href) return;
  element.setAttribute("href", href);
  element.setAttribute("rel", "noopener noreferrer");
  if (target) element.setAttribute("target", target);
}
function sanitizeStyle(styleText) {
  const probe = document.createElement("span");
  probe.setAttribute("style", styleText);
  const accepted = [];
  for (const property of ALLOWED_STYLES) {
    const value = probe.style.getPropertyValue(property).trim();
    if (!value || !isSafeStyleValue(property, value)) continue;
    accepted.push(`${property}: ${value}`);
  }
  return accepted.join("; ");
}
function isSafeStyleValue(property, value) {
  if (/url\s*\(|expression\s*\(|[<>]/i.test(value)) return false;
  if (property === "font-size") return SAFE_LENGTH.test(value);
  if (property === "line-height") return SAFE_LINE_HEIGHT.test(value);
  if (property === "text-align") return ALLOWED_TEXT_ALIGN.has(value);
  if (property === "font-family") return value.length <= 180;
  return property === "color" || property === "background-color";
}
function sanitizeLink(href) {
  const value = href.trim();
  if (!value) return null;
  if (/^(?:https?:|mailto:)/i.test(value)) return value;
  return null;
}

// src/components/RoyText.ts
var RoyText = class extends BaseComponent {
  inner = null;
  constructor(schema) {
    super(schema);
    this.inner = this.container.querySelector(".ptd-text__inner");
  }
  render() {
    this.container.classList.add("ptd-text");
    let inner = this.container.querySelector(".ptd-text__inner");
    if (!inner) {
      inner = document.createElement("div");
      inner.className = "ptd-text__inner";
      this.container.appendChild(inner);
    }
    this.inner = inner;
    const html = typeof this.schema.propValue === "string" ? this.schema.propValue : "";
    inner.innerHTML = sanitizeRichTextHtml(html);
  }
  setContent(html) {
    if (this.inner) {
      this.inner.innerHTML = sanitizeRichTextHtml(html);
    }
  }
};

// src/componentFactory.ts
var COMPONENT_CONSTRUCTORS = Object.freeze({
  RoySimpleText,
  RoyText,
  RoyLine,
  RoyRect,
  RoyCircle,
  RoyStar,
  RoyImage,
  RoyQRCode,
  RoyBarCode,
  RoyGroup,
  RoySimpleTable,
  RoyComplexTable
});
function createComponentInstance(schema) {
  const Constructor = COMPONENT_CONSTRUCTORS[schema.component];
  return Constructor ? new Constructor(schema) : null;
}
export {
  BaseComponent,
  RoyBarCode,
  RoyCircle,
  RoyComplexTable,
  RoyGroup,
  RoyImage,
  RoyLine,
  RoyQRCode,
  RoyRect,
  RoySimpleTable,
  RoySimpleText,
  RoyStar,
  RoyText,
  applyCssVars,
  componentStyleToCssVariables,
  createComponentInstance,
  injectStylesheet,
  sanitizeRichTextHtml
};
//# sourceMappingURL=index.js.map